import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCompany } from "@/hooks/use-active-company";
import { saveAssessment, setReportWorkflowState } from "@/lib/takst-api";
import { buildTakstImageRow, assertTakstImage, takstImagePath } from "@/lib/takst-images";
import { sha256Hex } from "@/features/electro-docs/hash";
import { PROJECT_BUCKET } from "@/features/electro-docs/storage";
import {
  canMarkReportReady,
  sectionLabel,
  totalCostRange,
  type TakstAssessmentInput,
  type TakstGradeCode,
} from "@/lib/takst";
import {
  assessmentProgress,
  blockerList,
  buildOutline,
  filterOutline,
  firstIncompleteId,
  missingPropertyBasics,
  nextIncompleteId,
  sectionSummaries,
  type TakstScreen,
} from "@/lib/takst-outline";
import {
  TAKST_WIZARD_STEPS,
  normalizePurpose,
  nextScreen,
  stepForScreen,
  type TakstPurpose,
  type TakstWizardStep,
} from "@/lib/takst-wizard";
import { MissingList, SectionItemList, SectionList } from "@/components/takst/FieldViews";
import { BasisPanel } from "@/components/takst/BasisPanel";
import { parseBasis, serializeBasis, basisIssues, type TakstBasis } from "@/lib/takst-basis";
import {
  gradeSummary,
  significantFindings,
  reportSectionLabel,
  reportSectionForItem,
} from "@/lib/takst-report-structure";
import {
  FactRow,
  PurposePicker,
  WizardFooter,
  WizardProgress,
} from "@/components/takst/WizardSteps";

import { ReportOutline } from "@/components/takst/ReportOutline";
import {
  PropertyPanel,
  type PropertyRecord,
  type PropertyUpdate,
} from "@/components/takst/PropertyPanel";
import {
  AssessmentPanel,
  type AssessmentImage,
  type AssessmentRow,
} from "@/components/takst/AssessmentPanel";

export const Route = createFileRoute("/_authenticated/takst/$id")({
  head: () => ({
    meta: [
      { title: "Takst – befaring | VERQO" },
      {
        name: "description",
        content: "Feltverktøy for befaring: punkter, avvik, konsekvens, kostnad og bilder.",
      },
      { property: "og:title", content: "Takst – befaring | VERQO" },
      {
        property: "og:description",
        content: "Feltverktøy for befaring: punkter, avvik, konsekvens, kostnad og bilder.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TakstDetailPage,
});

/** Oversetter venstremenyens nøkler til veiviserens skjermer. */
function outlineKeyToScreen(nodeKey: string): TakstScreen {
  if (nodeKey.startsWith("item:")) return nodeKey as TakstScreen;
  const group = nodeKey.replace(/^group:/, "");
  if (group === "must") return "missing";
  if (group === "closing") return "status";
  if (group === "basics") return "object";
  if (group === "overall") return "overall";
  return `section:${group}`;
}

/** Motsatt vei, slik at venstremenyen markerer riktig rad. */
function screenToOutlineKey(screen: TakstScreen): string {
  if (screen.startsWith("item:")) return screen;
  if (screen.startsWith("section:")) return `group:${screen.slice(8)}`;
  if (screen === "missing") return "group:must";
  if (screen === "status") return "group:closing";
  if (screen === "object" || screen === "basics") return "group:basics";
  if (screen === "overall") return "group:overall";
  return "";
}

function nok(value: number): string {
  return new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 0 }).format(value);
}

const WORKFLOW_LABEL: Record<string, string> = {
  draft: "Utkast",
  inspection_in_progress: "Befaring pågår",
  quality_review: "Kvalitetssikring",
  issued: "Utstedt",
  superseded: "Erstattet",
  withdrawn: "Trukket",
};

function TakstDetailPage() {
  const { id } = Route.useParams();
  const { companyId, user, activeModules } = useActiveCompany();
  const qc = useQueryClient();
  const [screen, setScreen] = useState<TakstScreen>("object");
  const [showAllMissing, setShowAllMissing] = useState(false);
  const [query, setQuery] = useState("");
  const [summary, setSummary] = useState("");

  const moduleActive = activeModules.includes("building_surveyor");

  const dataQ = useQuery({
    queryKey: ["takst-assignment", id, companyId],
    enabled: Boolean(companyId) && Boolean(id) && moduleActive,
    queryFn: async () => {
      const [assignmentRes, propertyRes, reportRes, inspectionRes] = await Promise.all([
        supabase.from("takst_assignments").select("*").eq("id", id).maybeSingle(),
        supabase.from("takst_properties").select("*").eq("assignment_id", id).maybeSingle(),
        supabase.from("takst_reports").select("*").eq("assignment_id", id).maybeSingle(),
        supabase
          .from("takst_inspections")
          .select("id, inspected_on, notes")
          .eq("assignment_id", id)
          .maybeSingle(),
      ]);
      if (assignmentRes.error) throw assignmentRes.error;
      const assignment = assignmentRes.data;
      if (!assignment) throw new Error("Fant ikke takstoppdraget");
      const report = reportRes.data;
      const inspection = inspectionRes.data;

      let customerName = "";
      if (assignment.customer_id) {
        const { data: customer } = await supabase
          .from("customers")
          .select("name")
          .eq("id", assignment.customer_id)
          .maybeSingle();
        customerName = customer?.name ?? "";
      }

      let reportNumber = "";
      if (report?.number_id) {
        const { data: numberRow } = await supabase
          .from("takst_numbers")
          .select("formatted_number")
          .eq("id", report.number_id)
          .maybeSingle();
        reportNumber = numberRow?.formatted_number ?? "";
      }

      let rows: AssessmentRow[] = [];
      let images: Record<string, AssessmentImage[]> = {};
      if (report) {
        const [assessmentsRes, itemsRes, gradesRes, imagesRes] = await Promise.all([
          supabase.from("takst_assessments").select("*").eq("report_id", report.id),
          supabase.from("takst_template_items").select("*").eq("template_id", report.template_id),
          supabase
            .from("takst_condition_grades")
            .select("id, code")
            .eq("scale_id", report.scale_id),
          supabase
            .from("takst_finding_images")
            .select("id, assessment_id, storage_path, caption, customer_visible")
            .eq("report_id", report.id),
        ]);
        const itemById = new Map((itemsRes.data ?? []).map((i) => [i.id, i]));
        const gradeById = new Map((gradesRes.data ?? []).map((g) => [g.id, g.code]));
        rows = (assessmentsRes.data ?? [])
          .map((a) => {
            const item = a.template_item_id ? itemById.get(a.template_item_id) : null;
            return {
              id: a.id,
              version: a.version,
              title: item?.title ?? a.section_key,
              guidance: item?.guidance ?? null,
              mandatory: item?.mandatory ?? true,
              sort_order: a.sort_order ?? item?.sort_order ?? 0,
              section_key: a.section_key,
              item_key: item?.item_key ?? null,
              state: a.state,
              grade_code: (a.grade_id
                ? (gradeById.get(a.grade_id) ?? null)
                : null) as TakstGradeCode | null,
              grade_exempt: Boolean(a.grade_exempt),
              observation: a.observation,
              deviation: a.deviation,
              consequence: a.consequence,
              recommended_action: a.recommended_action,
              not_assessed_reason: a.not_assessed_reason,
              cost_min: a.cost_min === null ? null : Number(a.cost_min),
              cost_max: a.cost_max === null ? null : Number(a.cost_max),
            } as AssessmentRow;
          })
          .sort((a, b) => a.sort_order - b.sort_order);

        const imageRows = imagesRes.data ?? [];
        const signed = await Promise.all(
          imageRows.map(async (img) => {
            const { data } = await supabase.storage
              .from(PROJECT_BUCKET)
              .createSignedUrl(img.storage_path, 3600);
            return { ...img, url: data?.signedUrl ?? null };
          }),
        );
        images = signed.reduce<Record<string, AssessmentImage[]>>((acc, img) => {
          const key = img.assessment_id ?? "";
          if (!key) return acc;
          (acc[key] ??= []).push({
            id: img.id,
            url: img.url,
            caption: img.caption,
            customer_visible: Boolean(img.customer_visible),
          });
          return acc;
        }, {});
      }
      return {
        assignment,
        property: propertyRes.data,
        report,
        inspection,
        customerName,
        reportNumber,
        rows,
        images,
      };
    },
  });

  const reportSummary = (dataQ.data?.report as { summary?: string | null } | null)?.summary ?? "";
  useEffect(() => {
    setSummary(reportSummary);
  }, [reportSummary]);

  const saveM = useMutation({
    mutationFn: async (args: { row: AssessmentRow; input: TakstAssessmentInput }) => {
      if (!user?.id) throw new Error("Mangler bruker");
      return saveAssessment({
        assessmentId: args.row.id,
        actorUserId: user.id,
        expectedVersion: args.row.version,
        state: args.input.state,
        gradeCode: args.input.grade_code,
        observation: args.input.observation,
        deviation: args.input.deviation,
        consequence: args.input.consequence,
        recommendedAction: args.input.recommended_action,
        notAssessedReason: args.input.not_assessed_reason,
        costMin: args.input.cost_min,
        costMax: args.input.cost_max,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["takst-assignment", id, companyId] });
      toast.success("Lagret");
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Kunne ikke lagre"),
  });

  const propertyM = useMutation({
    mutationFn: async (values: PropertyUpdate) => {
      const propertyId = dataQ.data?.property?.id;
      if (!propertyId) throw new Error("Mangler eiendom");
      const { error } = await supabase.from("takst_properties").update(values).eq("id", propertyId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["takst-assignment", id, companyId] });
      toast.success("Takstobjektet er lagret");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Kunne ikke lagre takstobjektet"),
  });

  const purposeM = useMutation({
    mutationFn: async (value: TakstPurpose) => {
      const { error } = await supabase
        .from("takst_assignments")
        .update({ purpose: value })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["takst-assignment", id, companyId] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Kunne ikke lagre formålet"),
  });

  const basisM = useMutation({
    mutationFn: async (values: TakstBasis) => {
      const inspectionId = dataQ.data?.inspection?.id;
      if (!inspectionId) throw new Error("Mangler befaring");
      const { error } = await supabase
        .from("takst_inspections")
        .update({ notes: serializeBasis(values) })
        .eq("id", inspectionId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["takst-assignment", id, companyId] });
      toast.success("Grunnlaget er lagret");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Kunne ikke lagre grunnlaget"),
  });

  const inspectedOnM = useMutation({
    mutationFn: async (value: string) => {
      const inspectionId = dataQ.data?.inspection?.id;
      if (!inspectionId) throw new Error("Mangler befaring");
      const { error } = await supabase
        .from("takst_inspections")
        .update({ inspected_on: value })
        .eq("id", inspectionId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["takst-assignment", id, companyId] });
      toast.success("Befaringsdato lagret");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Kunne ikke lagre befaringsdatoen"),
  });

  const summaryM = useMutation({
    mutationFn: async (value: string) => {
      const reportId = dataQ.data?.report?.id;
      if (!reportId) throw new Error("Mangler rapport");
      const { error } = await supabase
        .from("takst_reports")
        .update({ summary: value })
        .eq("id", reportId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["takst-assignment", id, companyId] });
      toast.success("Vurdering lagret");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Kunne ikke lagre vurderingen"),
  });

  const uploadM = useMutation({
    mutationFn: async (args: { row: AssessmentRow; file: File }) => {
      const report = dataQ.data?.report;
      const projectId = dataQ.data?.assignment.project_id;
      if (!report || !projectId || !companyId || !user?.id) {
        throw new Error("Bilder krever at oppdraget er koblet til et prosjekt.");
      }
      assertTakstImage(args.file);
      const path = takstImagePath({
        companyId,
        projectId,
        fileName: args.file.name,
        uuid: crypto.randomUUID(),
      });
      const sha256 = await sha256Hex(args.file);
      const up = await supabase.storage
        .from(PROJECT_BUCKET)
        .upload(path, args.file, { contentType: args.file.type, upsert: false });
      if (up.error) throw up.error;
      const row = buildTakstImageRow({
        companyId,
        reportId: report.id,
        assessmentId: args.row.id,
        storagePath: path,
        sha256,
        sizeBytes: args.file.size,
        mimeType: args.file.type,
        userId: user.id,
      });
      const { error } = await supabase.from("takst_finding_images").insert(row);
      if (error) {
        await supabase.storage.from(PROJECT_BUCKET).remove([path]);
        throw error;
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["takst-assignment", id, companyId] });
      toast.success("Bilde lagret");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Kunne ikke laste opp bildet"),
  });

  const workflowM = useMutation({
    mutationFn: async (next: "inspection_in_progress" | "quality_review") => {
      const report = dataQ.data?.report;
      if (!report || !user?.id) throw new Error("Mangler rapport");
      return setReportWorkflowState({ reportId: report.id, actorUserId: user.id, next });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["takst-assignment", id, companyId] });
      toast.success("Status oppdatert");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Kunne ikke endre status"),
  });

  const rows = useMemo(() => dataQ.data?.rows ?? [], [dataQ.data]);
  const outline = useMemo(() => buildOutline(rows), [rows]);
  const visible = useMemo(() => filterOutline(outline, query), [outline, query]);

  const selectedRow = screen.startsWith("item:")
    ? (rows.find((r) => r.id === screen.slice(5)) ?? null)
    : null;

  const cost = totalCostRange(rows);
  const ready = canMarkReportReady(rows);
  const canUpload = Boolean(dataQ.data?.assignment.project_id);
  const progress = assessmentProgress(rows);

  function go(next: TakstScreen) {
    setScreen(next);
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  }

  if (!moduleActive) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Takst er ikke aktivert for denne bedriften. Aktiver modulen under Innstillinger for å se
        takstoppdrag.
      </Card>
    );
  }

  if (dataQ.isLoading) {
    return <div className="py-20 text-center text-sm text-muted-foreground">Laster rapport…</div>;
  }

  if (dataQ.error || !dataQ.data) {
    return (
      <Card className="p-6">
        <p className="text-sm text-destructive">
          {dataQ.error instanceof Error ? dataQ.error.message : "Fant ikke takstoppdraget"}
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/takst">Tilbake til takstoppdrag</Link>
        </Button>
      </Card>
    );
  }

  const { assignment, property, report, inspection, customerName, reportNumber } = dataQ.data;
  const address = property?.address ?? assignment.title;
  const sections = sectionSummaries(rows);
  const blockers = blockerList(rows, property ?? null);
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const step = stepForScreen(screen);
  const nextIncomplete = firstIncompleteId(rows);
  const imageCount = Object.values(dataQ.data.images).reduce((n, list) => n + list.length, 0);
  const purpose = normalizePurpose(
    (assignment as { purpose?: string | null }).purpose ?? null,
  );
  const basicsMissing = missingPropertyBasics(property ?? null);
  const basis = parseBasis(inspection?.notes ?? null);
  const docMissing = basisIssues(basis);
  const grades = gradeSummary(rows);
  const findings = significantFindings(rows);
  const reportDate = report?.issued_at ?? report?.created_at ?? null;
  const dateText = (value: string | null) =>
    value ? new Date(value).toLocaleDateString("nb-NO") : "—";


  const sectionKey = screen.startsWith("section:") ? screen.slice(8) : null;
  const sectionItems = sectionKey ? (outline.find((g) => g.key === sectionKey)?.items ?? []) : [];

  function goStep(target: TakstWizardStep) {
    const meta = TAKST_WIZARD_STEPS.find((s) => s.key === target)!;
    if (target === "assess") {
      go(nextIncomplete ? (`item:${nextIncomplete}` as TakstScreen) : "status");
      return;
    }
    go(meta.screen);
  }

  const outlinePanel = (
    <ReportOutline
      groups={visible}
      query={query}
      onQueryChange={setQuery}
      selected={screenToOutlineKey(screen)}
      onSelect={(nodeKey) => go(outlineKeyToScreen(nodeKey))}
    />
  );

  return (
    <div className="-mt-2">
      <div className="mb-3">
        <Link
          to="/takst"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Takstoppdrag
        </Link>
      </div>

      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="hidden max-h-[calc(100vh-8rem)] overflow-hidden rounded-lg border bg-card lg:sticky lg:top-4 lg:block">
          {outlinePanel}
        </aside>

        <div className="min-w-0 space-y-3">
          <header className="card p-4">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
              <div className="min-w-0">
                <h1 className="truncate text-lg font-semibold">{address}</h1>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                  {[property?.postal_code, property?.city].filter(Boolean).join(" ")}
                </p>
              </div>
              <span className="chip shrink-0">
                {WORKFLOW_LABEL[report?.workflow_state ?? ""] ?? "–"}
              </span>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {progress.done} av {progress.total} punkter ferdig
                </span>
                <span>{pct} %</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
            <div className="mt-3">
              <WizardProgress current={step} onSelect={goStep} />
            </div>
          </header>

          {/* Steg 1 – Takstobjekt */}
          {(screen === "object" || screen === "basics") && (
            <section className="space-y-3">
              <div className="card p-4 sm:p-5">
                <h2 className="text-base font-semibold">Takstobjekt</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Sak, oppdragsgiver og datoer.
                </p>
                <div className="mt-4">
                  <PurposePicker
                    value={purpose}
                    onChange={(v) => purposeM.mutate(v)}
                    saving={purposeM.isPending}
                  />
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs font-medium text-muted-foreground">
                      Befaringsdato
                    </span>
                    <input
                      type="date"
                      className="input-base"
                      defaultValue={inspection?.inspected_on ?? ""}
                      disabled={inspectedOnM.isPending || !inspection}
                      onChange={(e) => {
                        if (e.target.value) inspectedOnM.mutate(e.target.value);
                      }}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs font-medium text-muted-foreground">
                      Eierform
                    </span>
                    <input
                      className="input-base"
                      defaultValue={basis.eierform}
                      disabled={!inspection}
                      onBlur={(e) => {
                        if (e.target.value !== basis.eierform)
                          basisM.mutate({ ...basis, eierform: e.target.value });
                      }}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs font-medium text-muted-foreground">
                      Hjemmelshaver
                    </span>
                    <input
                      className="input-base"
                      defaultValue={basis.hjemmelshaver}
                      disabled={!inspection}
                      onBlur={(e) => {
                        if (e.target.value !== basis.hjemmelshaver)
                          basisM.mutate({ ...basis, hjemmelshaver: e.target.value });
                      }}
                    />
                  </label>
                </div>
                <div className="mt-3">
                  <FactRow label="Oppdrag" value={assignment.title} />
                  <FactRow label="Oppdragsgiver" value={customerName || "—"} />
                  <FactRow label="Rapportnummer" value={reportNumber || "Tildeles ved utstedelse"} />
                  <FactRow label="Rapportdato" value={dateText(reportDate)} />
                </div>
              </div>

              <PropertyPanel
                property={(property ?? null) as PropertyRecord | null}
                saving={propertyM.isPending}
                onSave={(values) => propertyM.mutate(values)}
              />

              <WizardFooter
                primaryLabel="Neste"
                onPrimary={() => go(nextScreen("object", { firstIncompleteId: nextIncomplete }))}
                secondary={
                  <Button
                    variant="outline"
                    className="h-12"
                    onClick={() =>
                      go(
                        nextIncomplete
                          ? (`item:${nextIncomplete}` as TakstScreen)
                          : ("status" as TakstScreen),
                      )
                    }
                  >
                    Fortsett takst
                  </Button>
                }
              />
            </section>
          )}

          {/* Steg 2 – Grunnlag og dokumentasjon */}
          {screen === "grunnlag" && (
            <section className="space-y-3">
              <BasisPanel
                basis={basis}
                saving={basisM.isPending}
                onSave={(values) => basisM.mutate(values)}
              />
              <div className="card p-4 sm:p-5">
                <h3 className="text-sm font-semibold">Vedlegg og bilder</h3>
                <div className="mt-2">
                  <FactRow
                    label="Matrikkel"
                    value={
                      basicsMissing.length > 0
                        ? `Mangler ${basicsMissing.join(", ")}`
                        : `Gnr ${property?.cadastre_gnr} / Bnr ${property?.cadastre_bnr}`
                    }
                  />
                  <FactRow label="Bilder" value={`${imageCount} lagret`} />
                  <FactRow
                    label="Vedlegg"
                    value={
                      assignment.project_id ? "Ligger på prosjektet" : "Ingen prosjekt koblet"
                    }
                  />
                </div>
                {assignment.project_id && (
                  <Button asChild variant="outline" size="sm" className="mt-3">
                    <Link to="/projects/$id" params={{ id: assignment.project_id }}>
                      Åpne dokumentasjon
                    </Link>
                  </Button>
                )}
              </div>
              <WizardFooter
                primaryLabel="Neste"
                onPrimary={() => go(nextScreen("basis", { firstIncompleteId: nextIncomplete }))}
                secondary={
                  <Button variant="outline" className="h-12" onClick={() => go("object")}>
                    Tilbake
                  </Button>
                }
              />
            </section>
          )}


          {/* Steg 3 – Befaring */}
          {screen === "sections" && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold">Befaring</h2>
              <SectionList
                sections={sections}
                onOpen={(key) => go(`section:${key}` as TakstScreen)}
              />
              <WizardFooter
                primaryLabel="Neste"
                onPrimary={() => go(nextScreen("survey", { firstIncompleteId: nextIncomplete }))}
                secondary={
                  <Button variant="outline" className="h-12" onClick={() => go("grunnlag")}>
                    Tilbake
                  </Button>
                }
              />
            </section>
          )}

          {sectionKey && (
            <section className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">{sectionLabel(sectionKey)}</h2>
                <Button variant="ghost" size="sm" onClick={() => go("sections")}>
                  Alle seksjoner
                </Button>
              </div>
              <SectionItemList
                items={sectionItems}
                onOpen={(itemId) => go(`item:${itemId}` as TakstScreen)}
              />
            </section>
          )}

          {screen === "missing" && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold">
                Dette må fylles ut før rapporten kan meldes klar
              </h2>
              <MissingList
                blockers={blockers}
                showAll={showAllMissing}
                onShowAll={() => setShowAllMissing(true)}
                onOpen={(target) => go(target)}
              />
            </section>
          )}

          {/* Steg 4 – Vurder punkt */}
          {selectedRow && (
            <AssessmentPanel
              key={selectedRow.id}
              row={selectedRow}
              sectionLabel={sectionLabel(selectedRow.section_key)}
              images={dataQ.data.images[selectedRow.id] ?? []}
              canUploadImages={canUpload}
              uploadHint="Koble oppdraget til et prosjekt for å lagre bilder"
              saving={saveM.isPending}
              uploading={uploadM.isPending}
              onBack={() => go("sections")}
              onSave={(input, opts) => {
                saveM.mutate(
                  { row: selectedRow, input },
                  {
                    onSuccess: () => {
                      if (!opts.next) return;
                      const updated = rows.map((r) =>
                        r.id === selectedRow.id ? { ...r, ...input } : r,
                      );
                      const next = nextIncompleteId(updated, selectedRow.id);
                      go(next ? (`item:${next}` as TakstScreen) : "status");
                    },
                  },
                );
              }}
              onUpload={(file) => uploadM.mutate({ row: selectedRow, file })}
            />
          )}

          {screen === "overall" && (
            <section className="card p-4 sm:p-5">
              <h2 className="text-base font-semibold">Samlet vurdering</h2>
              <label className="mt-3 block text-sm">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">
                  Oppsummering
                </span>
                <textarea
                  className="input-base min-h-28"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                />
              </label>
              <div className="mt-4 border-t pt-3">
                <Button
                  disabled={summaryM.isPending || !report}
                  onClick={() => summaryM.mutate(summary)}
                >
                  {summaryM.isPending ? "Lagrer…" : "Lagre"}
                </Button>
              </div>
            </section>
          )}

          {/* Steg 5 – Kontroll */}
          {screen === "status" && (
            <section className="card p-4 sm:p-5">
              <h2 className="text-base font-semibold">Kontroll</h2>

              <div className="mt-3 grid grid-cols-4 gap-2">
                {grades.map((g) => (
                  <div key={g.key} className="rounded-lg border border-border p-2 text-center">
                    <p className="truncate text-xs text-muted-foreground">{g.key}</p>
                    <p className="text-lg font-semibold tabular-nums">{g.count}</p>
                  </div>
                ))}
              </div>

              {findings.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    Vesentlige funn (TG2 og TG3)
                  </p>
                  <ul className="mt-1 grid gap-1">
                    {findings.map((f) => (
                      <li key={f.id} className="text-sm">
                        <span className="font-medium">{f.title}</span>
                        <span className="text-muted-foreground">
                          {" "}
                          · {reportSectionLabel(reportSectionForItem(f.item_key, f.section_key))} ·{" "}
                          {f.grade_code ?? "TGIU"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {docMissing.length > 0 && (
                <p className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-700">
                  Grunnlag mangler: {docMissing.join(", ")}
                </p>
              )}

              {ready ? (
                <p className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm font-medium text-emerald-700">
                  Rapporten kan meldes klar.
                </p>
              ) : (
                <div className="mt-3">
                  <MissingList
                    blockers={blockers}
                    showAll={showAllMissing}
                    onShowAll={() => setShowAllMissing(true)}
                    onOpen={(target) => go(target)}
                  />
                </div>
              )}
              {cost.max > 0 && (
                <p className="mt-3 text-sm text-muted-foreground">
                  Kostnad: {nok(cost.min)}–{nok(cost.max)} kr
                </p>
              )}
              {report?.pdf_storage_path && (
                <span className="chip mt-3 inline-flex border-border">
                  <FileText className="mr-1 h-3.5 w-3.5" /> PDF klar
                </span>
              )}

              <WizardFooter
                primaryLabel="Meld klar"
                disabled={!report || !ready || workflowM.isPending}
                onPrimary={() => workflowM.mutate("quality_review")}
                secondary={
                  <Button variant="outline" className="h-12" onClick={() => go("sections")}>
                    Tilbake
                  </Button>
                }
              />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
